import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  deactivateWorkforce,
  setSelectedWorkforceMeasure,
  activateAnalysisPinMode,
  deactivateAnalysisPinMode,
  clearAnalysisPinLocation,
} from '../../store/actions';
import {
  ViewerPrimaryColorUserStyle,
  ViewerSecondaryColorUserStyle,
} from '../UserCustomization';
import './WorkforceModal.css';

import { OCCUPATION_DICT, INDUSTRY_DICT, REPORT_API_ENDPOINTS } from '../../constants';
import { storage } from '../../config';

class WorkforceModal extends React.Component {
  static getDerivedStateFromProps(props, state) {
    const updates = {};

    // Handle modal closing
    if (!props.workforce && state.isActive && !state.isClosing) {
      updates.isActive = false;
      updates.isClosing = true;
      // If modal is closing and pin drop mode is active, deactivate it
      if (props.analysisPinModeActive) {
        props.deactivateAnalysisPinMode();
      }
    }

    // Sync component state with Redux store's selectedWorkforceMeasure
    const selectedMeasure = props.selectedMeasure;
    if (selectedMeasure && selectedMeasure.code) {
      // Handle redux has state but component does not
      const table = selectedMeasure.table;
      const code = selectedMeasure.code;
      const itemId = `${table}-${code}`;

      if (state.selectedItem !== itemId) {
        updates.selectedItem = itemId;

        // Update active tab
        if ((table === 'occ' && state.activeTab !== 'Occupation') ||
            (table === 'ind' && state.activeTab !== 'Industry')) {
          updates.activeTab = table === 'occ' ? 'Occupation' : 'Industry';
        }
      }

      // Sync metric type
      if (state.selectedMetric !== selectedMeasure.metricType) {
        updates.selectedMetric = selectedMeasure.metricType;
      }
    }

    // Determine analysisTabMode based on Redux state
    // Pin mode active
    if (props.analysisPinModeActive) {
      if (state.analysisTabMode !== 'awaitingPinDrop') {
        updates.analysisTabMode = 'awaitingPinDrop';
      }
      // Ensure coordinates are cleared when in pin drop mode
      if (state.analysisPinCoords !== null) {
        updates.analysisPinCoords = null;
      }
    }
    // When pin location is set (and mode is not active)
    else if (props.analysisPinLocation) {
      if (state.analysisTabMode !== 'pinSelected') {
        updates.analysisTabMode = 'pinSelected';
      }
      if (state.analysisPinCoords !== props.analysisPinLocation) {
        updates.analysisPinCoords = props.analysisPinLocation;
      }
    }
    // When neither pin mode is active nor location set
    else {
      if (state.analysisTabMode !== 'idle') {
        updates.analysisTabMode = 'idle';
      }
      if (state.analysisPinCoords !== null) {
        updates.analysisPinCoords = null;
      }
    }

    return Object.keys(updates).length ? updates : null;
  }

  /* ----------------------- constructor -------------------------- */
  constructor(props) {
    super(props);
    this.state = {
      activeTab: 'Occupation',
      selectedMetric: 'wages',
      selectedItem: null,
      isActive: false,
      isClosing: false,
      analysisTabMode: 'idle',
      analysisPinCoords: null,
      isGeneratingReport: false,
      driveType: 'mins',
      driveValues: [3, 5, 8], // we can change these, just arbitrary
      addressLine1: '',
      addressLine2: '',
      reportFormat: 'pdf',
    };
  }

  /* -------------------- normal lifecycle ----------------------- */
  componentDidMount() {
    setTimeout(() => {
      this.setState({ isActive: true });

      const { selectedMeasure, analysisPinModeActive, analysisPinLocation } = this.props;
      if (selectedMeasure && selectedMeasure.code) {
        const table = selectedMeasure.table;
        const code = selectedMeasure.code;
        const itemId = `${table}-${code}`;

        this.setState({
          selectedItem: itemId,
          selectedMetric: selectedMeasure.metricType,
          activeTab: table === 'occ' ? 'Occupation' : 'Industry',
        });
      }

      // Initialize analysis tab state from Redux
      let initialAnalysisTabMode = 'idle';
      if (analysisPinModeActive) {
        initialAnalysisTabMode = 'awaitingPinDrop';
      } else if (analysisPinLocation) {
        initialAnalysisTabMode = 'pinSelected';
      }
      this.setState({
        analysisTabMode: initialAnalysisTabMode,
        analysisPinCoords: analysisPinLocation,
      });
    }, 50);
  }

  componentDidUpdate(prevProps) {
    // Explicitly handle updates to analysis pin state
    if (prevProps.analysisPinModeActive !== this.props.analysisPinModeActive) {
      this.setState({
        analysisTabMode: this.props.analysisPinModeActive ? 'awaitingPinDrop' :
                         (this.props.analysisPinLocation ? 'pinSelected' : 'idle'),
      });
    }

    if (prevProps.analysisPinLocation !== this.props.analysisPinLocation) {
      if (this.props.analysisPinLocation) {
        this.setState({
          analysisTabMode: 'pinSelected',
          analysisPinCoords: this.props.analysisPinLocation,
        });
      } else if (!this.props.analysisPinModeActive) {
        this.setState({
          analysisTabMode: 'idle',
          analysisPinCoords: null,
        });
      }
    }
  }

  componentWillUnmount() {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
    }
    // Ensure pin drop mode is deactivated if modal is unmounted while active
    if (this.props.analysisPinModeActive) {
      this.props.deactivateAnalysisPinMode();
    }
  }

  /* --------------------- handlers ------------------------------- */
  handleClose = () => {
    this.setState({ isActive: false, isClosing: true });
    this.closeTimeout = setTimeout(() => {
      this.props.deactivateWorkforce();
    }, 200);
  };

  handleTabChange = value => {
    // If switching away from Analysis tab and pin drop mode is active, deactivate it.
    if (this.state.activeTab === 'Analysis' && value !== 'Analysis' && this.props.analysisPinModeActive) {
      this.props.deactivateAnalysisPinMode();
    }

    // Simply change the active tab. Do NOT clear selectedItem so that toggle state
    // persists when returning to the original tab.
    this.setState({ activeTab: value });
  };

  handleMetricChange = value => {
    this.setState({ selectedMetric: value }, () => {
      const { selectedItem } = this.state;
      if (selectedItem) {
        const [type, code] = selectedItem.split('-');
        const measure = {
          table: type === 'occ' ? 'occ' : 'ind',
          code: parseInt(code, 10),
          metricType: value,
        };
        this.props.setSelectedWorkforceMeasure(measure);
      } else {
        // When no item is selected, don't include a code in the measure
        this.props.setSelectedWorkforceMeasure({
          table: this.state.activeTab === 'Occupation' ? 'occ' : 'ind',
          metricType: value,
        });
      }
    });
  };

  handleItemToggle = itemId => {
    this.setState(
      prevState => ({
        selectedItem: prevState.selectedItem === itemId ? null : itemId,
      }),
      () => {
        // If an item is selected, set the measure with code
        if (this.state.selectedItem) {
          const [type, code] = this.state.selectedItem.split('-');
          const measure = {
            table: type === 'occ' ? 'occ' : 'ind',
            code: parseInt(code, 10),
            metricType: this.state.selectedMetric,
          };
          this.props.setSelectedWorkforceMeasure(measure);
        }
        // If deselected, set measure with no code to prevent map rendering
        else {
          this.props.setSelectedWorkforceMeasure({
            table: this.state.activeTab === 'Occupation' ? 'occ' : 'ind',
            metricType: this.state.selectedMetric,
          });
        }
      },
    );
  };

  handleAnalysisModeToggle = () => {
    const { analysisTabMode } = this.state; // Current UI mode
    const { activateAnalysisPinMode, deactivateAnalysisPinMode } = this.props;

    if (analysisTabMode === 'idle') {
      activateAnalysisPinMode(); // Clear via reducer too
    } else if (analysisTabMode === 'awaitingPinDrop') {
      deactivateAnalysisPinMode();
    } else if (analysisTabMode === 'pinSelected') {
      activateAnalysisPinMode();
    }
  };

  /* Clear the currently selected pin/location entirely */
  handleClearLocation = () => {
    const { deactivateAnalysisPinMode, clearAnalysisPinLocation } = this.props;

    // Ensure pin-drop mode is turned off
    if (this.props.analysisPinModeActive) {
      deactivateAnalysisPinMode();
    }

    // Clear the location in the store
    clearAnalysisPinLocation();

    // Immediately update local state for snappier UI feedback
    this.setState({
      analysisTabMode: 'idle',
      analysisPinCoords: null,
    });
  };

  handleDriveTypeChange = e => {
    this.setState({ driveType: e.target.value });
  }

  handleDriveValueChange = (index, rawValue) => {
    const maxVal = 90;
    const newDriveValues = [...this.state.driveValues];

    // Allow user to clear the field while typing
    if (rawValue === '') {
      newDriveValues[index] = '';
    } else {
      const numeric = parseInt(rawValue, 10);
      if (Number.isNaN(numeric)) {
        newDriveValues[index] = '';
      } else {
        newDriveValues[index] = Math.min(maxVal, Math.max(1, numeric));
      }
    }

    this.setState({ driveValues: newDriveValues });
  }

  // Handle change for report format
  handleFormatChange = e => {
    this.setState({ reportFormat: e.target.value });
  }

  handleAddressChange = (field, value) => {
    this.setState({ [field]: value });
  }

  handleGenerateReport = coordinates => {
    // Show loading state
    this.setState({ isGeneratingReport: true });

    const { driveType, driveValues, addressLine1, addressLine2, reportFormat } = this.state;

    // Prepare request
    const payload = {
      location: {
        lat: coordinates.lat,
        lng: coordinates.lng,
        title: addressLine1 || 'Selected Location',
        subtitle: addressLine2 || 'Map Viewer Selection',
      },
      driveParams: {
        type: driveType,
        vals: (() => {
          const maxVal = 90;
          const sanitized = driveValues
            .map(v => parseInt(v, 10))
            .filter(n => !Number.isNaN(n) && n >= 1 && n <= maxVal);

          // make unique and sort
          return Array.from(new Set(sanitized)).sort((a, b) => a - b);
        })(),
      },
      logoUrl: this.props.logoUrl || '',
    };

    // Call endpoint with desired format
    const fetchUrl = `${REPORT_API_ENDPOINTS.PIN_WORKFORCE_REPORT}?format=${reportFormat}`;

    fetch(fetchUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      return response.blob();
    })
    .then(blob => {
      // Create a URL for the blob
      const blobUrl = window.URL.createObjectURL(blob);

      const downloadLink = document.createElement('a');
      downloadLink.href = blobUrl;

      // prep download and download
      const fileName = `Workforce_Report_${coordinates.lat.toFixed(4)}_${coordinates.lng.toFixed(4)}.${reportFormat}`;
      downloadLink.download = fileName;

      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 30000);

      // Not loading anymore
      this.setState({ isGeneratingReport: false });
    })
    .catch(error => {
      console.error('Error fetching report:', error);
      alert('Failed to generate report. Please try again later.');
      this.setState({ isGeneratingReport: false });
    });
  }

  /* Clear selected occupation/industry heatmap */
  handleClearSelection = () => {
    const { activeTab, selectedMetric } = this.state;
    const { selectedMeasure } = this.props;

    // Determine which table to target for clearing
    const tableToClear = selectedMeasure && selectedMeasure.table
      ? selectedMeasure.table
      : (activeTab === 'Occupation' ? 'occ' : 'ind');

    // Reset local state for immediate UI feedback
    this.setState({ selectedItem: null }, () => {
      // Dispatch with no code to remove heatmap layer
      this.props.setSelectedWorkforceMeasure({
        table: tableToClear,
        metricType: selectedMetric,
      });
    });
  };

  /* -------------------- helpers ------------------------------- */
  renderTabContent(items, tabName) {
    // Get analysis state from component state, which is synced with Redux
    const {
      selectedMetric,
      selectedItem,
      analysisTabMode,
      analysisPinCoords,
      isGeneratingReport,
      driveType,
      driveValues,
      addressLine1,
      addressLine2,
      reportFormat,
    } = this.state;

    if (tabName === 'Occupation' || tabName === 'Industry') {
        // Heat-map considered active if Redux measure has a code
      const isSelectionActive = this.props.selectedMeasure && this.props.selectedMeasure.code !== undefined && this.props.selectedMeasure.code !== null;

      return (
        <div className="occupation-content">
          {!isSelectionActive ? (
            <div><b>Select an {tabName === 'Occupation' ? 'occupation' : 'industry'} to display a heat map.</b></div>
                ) : (
                  <button
                    type="button"
                    className="workforce-modal-button"
                    onClick={this.handleClearSelection}
                    style={{
                      padding: '6px 10px',
                      cursor: 'pointer',
                      backgroundColor: '#dc3545', /* danger/red tone */
                      color: 'white',
                      border: 'none',
                      borderRadius: '3px',
                      marginBottom: '10px',
                    }}
                  >
                    Clear Selection
                  </button>
                )}
          <div className="occupation-metrics">
            <div className="radio-group">
              <div className="radio-option">
                <input
                  type="radio"
                  id={`wagesMetric${tabName}`}
                  name={`${tabName.toLowerCase()}Metric`}
                  value="wages"
                  checked={selectedMetric === 'wages'}
                  onChange={e => this.handleMetricChange(e.target.value)}
                />
                <label htmlFor={`wagesMetric${tabName}`}>Annual Average Wages</label>
              </div>

              <div className="radio-option">
                <input
                  type="radio"
                  id={`employmentMetric${tabName}`}
                  name={`${tabName.toLowerCase()}Metric`}
                  value="employment"
                  checked={selectedMetric === 'employment'}
                  onChange={e => this.handleMetricChange(e.target.value)}
                />
                <label htmlFor={`employmentMetric${tabName}`}>Employment Count</label>
              </div>
            </div>
          </div>

          <div className="occupation-list">
            {items.map(item => (
              <div key={item.id} className="toggle-row">
                <label
                  className="toggle-label"
                  htmlFor={`toggle-${item.id}`}
                >
                  ({item.code}) {item.name}
                  <div className="toggle-switch">
                    <input
                      type="checkbox"
                      id={`toggle-${item.id}`}
                      checked={selectedItem === item.id}
                      onClick={e => {
                        e.stopPropagation();
                        this.handleItemToggle(item.id);
                      }}
                      onChange={() => {}}
                    />
                    <span className="slider" />
                  </div>
                </label>
              </div>
                ))}
          </div>
        </div>
      );
    }

    if (tabName === 'Analysis') {
      let buttonText = 'Select Location for Report';
      let instructionText = 'Click the button to activate pin-drop mode. Then, click on the map to choose a location for the report.';

      if (analysisTabMode === 'awaitingPinDrop') {
        buttonText = 'Cancel Pin Selection Mode';
        instructionText = 'The map is now active for pin selection. Click on your desired location on the map, or cancel.';
      } else if (analysisTabMode === 'pinSelected' && analysisPinCoords) {
        buttonText = 'Change Selected Location';

            // location info module
        instructionText = (
          <div
            className="selected-location-info" style={{
              margin: '15px 0',
              padding: '12px',
              backgroundColor: '#f8f9fa',
              borderRadius: '4px',
              border: '1px solid #e9ecef',
            }}
          >
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Selected Location:</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              <div style={{ minWidth: '140px' }}>
                <span style={{ color: '#666' }}>Latitude:</span> <span style={{ fontWeight: '500' }}>{analysisPinCoords.lat.toFixed(6)}</span>
              </div>
              <div style={{ minWidth: '140px' }}>
                <span style={{ color: '#666' }}>Longitude:</span> <span style={{ fontWeight: '500' }}>{analysisPinCoords.lng.toFixed(6)}</span>
              </div>
            </div>
          </div>
            );
      }

        // Explicitly check if we should show the report button
      const showReportButton = (analysisTabMode === 'pinSelected' && analysisPinCoords);

        // Common styles for inputs
      const inputStyle = {
        padding: '8px',
        margin: '5px 0',
        borderRadius: '3px',
        border: '1px solid #ccc',
        width: '100%',
      };

      const labelStyle = {
        fontWeight: 'bold',
        marginTop: '10px',
        display: 'block',
      };

      const numberInputStyle = {
        ...inputStyle,
        width: '70px',
      };

      return (
        <div className="analysis-content" style={{ padding: '15px' }}>
          <h4
            style={{
              fontSize: '1.4rem',
              marginBottom: '20px',
              fontWeight: '600',
              borderBottom: '1px solid #eee',
              paddingBottom: '10px',
            }}
          >Location-Based Analysis Report</h4>

          {typeof instructionText === 'string' ? (
            <p style={{ marginBottom: '15px' }}>{instructionText}</p>
                ) : (
                  instructionText
                )}

          <div className="analysis-actions" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {/* Pin Selection Button */}
            <button
              type="button"
              className="workforce-modal-button"
              onClick={this.handleAnalysisModeToggle}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                backgroundColor: '#4a4a4a',
                color: 'white',
                border: 'none',
                borderRadius: '3px',
              }}
            >
              {buttonText}
            </button>

            {/* Clear Location Button – only shown when a location is currently selected */}
            {analysisTabMode === 'pinSelected' && (
            <button
              type="button"
              className="workforce-modal-button"
              onClick={this.handleClearLocation}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                backgroundColor: '#dc3545', /* danger/red tone */
                color: 'white',
                border: 'none',
                borderRadius: '3px',
              }}
            >
                        Clear Selected Location
                      </button>
                    )}

            {/* Report Options Form - only shown when a pin is selected */}
            {showReportButton && (
            <div className="report-options" style={{ marginTop: '20px', borderTop: '1px solid #eee', paddingTop: '15px' }}>

              {/* Custom Address */}
              <div className="address-inputs">
                <label style={labelStyle} htmlFor="addressLine1">Title:</label>
                <input
                  id="addressLine1"
                  type="text"
                  style={inputStyle}
                  value={addressLine1}
                  onChange={e => this.handleAddressChange('addressLine1', e.target.value)}
                  placeholder="Custom Title"
                />

                <label style={labelStyle} htmlFor="addressLine2">Subtitle:</label>
                <input
                  id="addressLine2"
                  type="text"
                  style={inputStyle}
                  value={addressLine2}
                  onChange={e => this.handleAddressChange('addressLine2', e.target.value)}
                  placeholder="Custom Subtitle"
                />
              </div>

              {/* Drive Type Selection */}
              <div className="drive-type" style={{ marginTop: '15px' }}>
                <label style={labelStyle}>Analysis Boundary Type:</label>
                <div style={{ display: 'flex', gap: '15px', margin: '10px 0' }}>
                  <label>
                    <input
                      type="radio"
                      name="driveType"
                      value="mins"
                      checked={driveType === 'mins'}
                      onChange={this.handleDriveTypeChange}
                    />
                    {' '}Drive Time (minutes)
                                    </label>
                  <label>
                    <input
                      type="radio"
                      name="driveType"
                      value="miles"
                      checked={driveType === 'miles'}
                      onChange={this.handleDriveTypeChange}
                    />
                    {' '}Drive Distance (miles)
                                    </label>
                </div>
              </div>

              {/* Drive Values */}
              <div className="drive-values">
                <label style={labelStyle}>
                  {driveType === 'mins' ? 'Drive Times (1-90 minutes):' : 'Drive Distances (1-90 miles):'}
                </label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', margin: '10px 0' }}>
                  {driveValues.map((value, index) => (
                    <input
                      key={index}
                      type="number"
                      min="1"
                      max="90"
                      style={numberInputStyle}
                      value={value}
                      onChange={e => this.handleDriveValueChange(index, e.target.value)}
                    />
                                    ))}
                </div>
                <p style={{ fontSize: '0.85em', color: '#666', marginTop: '5px' }}>
                                    Enter up to 3 values between 1 and {driveType === 'mins' ? '90 minutes' : '90 miles'}.
                                </p>
              </div>

              {/* Report Format Selection */}
              <div className="report-format" style={{ marginTop: '15px' }}>
                <label style={labelStyle}>Report Format:</label>
                <div style={{ display: 'flex', gap: '15px', margin: '10px 0' }}>
                  <label>
                    <input
                      type="radio"
                      name="reportFormat"
                      value="pdf"
                      checked={reportFormat === 'pdf'}
                      onChange={this.handleFormatChange}
                    />
                    {' '}PDF
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="reportFormat"
                      value="xlsx"
                      checked={reportFormat === 'xlsx'}
                      onChange={this.handleFormatChange}
                    />
                    {' '}Excel
                  </label>
                </div>
              </div>

              {/* Generate Report Button */}
              <div style={{ marginTop: '20px' }}>
                <button
                  type="button"
                  className="workforce-generate-report-button"
                  onClick={() => this.handleGenerateReport(analysisPinCoords)}
                  disabled={isGeneratingReport}
                  style={{
                    padding: '10px 15px',
                    cursor: isGeneratingReport ? 'wait' : 'pointer',
                    backgroundColor: isGeneratingReport ? '#6c757d' : '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '3px',
                    fontWeight: 'bold',
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {isGeneratingReport ? 'Generating Report...' : 'Generate Analysis Report'}
                </button>
              </div>
            </div>
                    )}
          </div>
        </div>
      );
    }

    return null;
  }

  /* ----------------------- render ------------------------------ */
  render() {
    // Hide entire component if workforce reports are disabled in admin settings
    if (!this.props.showWorkforceReports) {
      return null;
    }

    const { activeTab, isActive, isClosing } = this.state;

    const occupations = [];
    Object.entries(OCCUPATION_DICT).forEach(([code, label]) => {
      occupations.push({ id: `occ-${code}`, name: label, code });
    });

    const industries = [];
    Object.entries(INDUSTRY_DICT).forEach(([code, label]) => {
      industries.push({ id: `ind-${code}`, name: label, code });
    });

    return (
      <div
        className={`workforce-wrapper ${isActive ? 'active' : ''} ${
          isClosing ? 'closing' : ''
        }`}
      >
        <ViewerSecondaryColorUserStyle domStyle="backgroundColor">
          <div className="toolbox-header">
            <div>Workforce</div>
            <div className="toolbox-actions">
              <a
                className="toolbox-close"
                onClick={this.handleClose}
                tabIndex="0"
              >
                ✕
              </a>
            </div>
          </div>
        </ViewerSecondaryColorUserStyle>

        <div className="toolbox-content">
          <div className="form-group">
            <div className="switch-field">
              <input
                type="radio"
                id="occupationTab"
                name="workforceTab"
                value="Occupation"
                checked={activeTab === 'Occupation'}
                onChange={e => this.handleTabChange(e.target.value)}
              />
              <ViewerPrimaryColorUserStyle
                domStyle={activeTab === 'Occupation' && 'borderBottomColor'}
              >
                <label htmlFor="occupationTab">Occupation</label>
              </ViewerPrimaryColorUserStyle>

              <input
                type="radio"
                id="industryTab"
                name="workforceTab"
                value="Industry"
                checked={activeTab === 'Industry'}
                onChange={e => this.handleTabChange(e.target.value)}
              />
              <ViewerPrimaryColorUserStyle
                domStyle={activeTab === 'Industry' && 'borderBottomColor'}
              >
                <label htmlFor="industryTab">Industry</label>
              </ViewerPrimaryColorUserStyle>

              <input
                type="radio"
                id="analysisTab"
                name="workforceTab"
                value="Analysis"
                checked={activeTab === 'Analysis'}
                onChange={e => this.handleTabChange(e.target.value)}
              />
              <ViewerPrimaryColorUserStyle
                domStyle={activeTab === 'Analysis' && 'borderBottomColor'}
              >
                <label htmlFor="analysisTab">Analysis</label>
              </ViewerPrimaryColorUserStyle>
            </div>
          </div>

          <div className="tab-content">
            {activeTab === 'Occupation' &&
              this.renderTabContent(occupations, 'Occupation')}
            {activeTab === 'Industry' &&
              this.renderTabContent(industries, 'Industry')}
            {activeTab === 'Analysis' &&
              this.renderTabContent(null, 'Analysis')}
          </div>
        </div>
      </div>
    );
  }
}

WorkforceModal.propTypes = {
  deactivateWorkforce: PropTypes.func.isRequired,
  setSelectedWorkforceMeasure: PropTypes.func.isRequired,
  selectedMeasure: PropTypes.shape({
    table: PropTypes.string,
    code: PropTypes.number,
    metricType: PropTypes.string,
  }),
  workforce: PropTypes.bool,
  analysisPinModeActive: PropTypes.bool.isRequired,
  analysisPinLocation: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  activateAnalysisPinMode: PropTypes.func.isRequired,
  deactivateAnalysisPinMode: PropTypes.func.isRequired,
  clearAnalysisPinLocation: PropTypes.func.isRequired,
  showWorkforceReports: PropTypes.bool.isRequired,
  logoUrl: PropTypes.string,
};

const mapStateToProps = state => {
  return {
    workforce: state.workforce,
    selectedMeasure: state.selectedWorkforceMeasure,
    analysisPinModeActive: state.analysisPinModeActive,
    analysisPinLocation: state.analysisPinLocation,
    showWorkforceReports: !!(state.viewer && state.viewer.adminSettings && state.viewer.adminSettings.lightcastWorkforceData),
    logoUrl: state.viewer && state.viewer.logo ? `${storage.cloudStoragePath}/${state.viewer.logo}` : '',
  };
};

export default connect(mapStateToProps, {
  deactivateWorkforce,
  setSelectedWorkforceMeasure,
  activateAnalysisPinMode,
  deactivateAnalysisPinMode,
  clearAnalysisPinLocation,
})(WorkforceModal);



// WEBPACK FOOTER //
// ./src/components/List/WorkforceModal.js